import { Button, Stack, Paper, Typography, Box, Alert, Tooltip, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { baseUrl } from '../constants/endpoints';
import { useEffect, useState } from 'react';
import { SearchBar } from './Grid/SearchBar';
import { Drawer } from './Drawer/Drawer';
import { Activity } from '@oskarjiang/matchi-types';
import { Grid } from './Grid/Grid';
import { calculateWeeksToGivenDate } from '../constants/numbers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TodayIcon from '@mui/icons-material/Today';

export const WeekGrid = () => {
	const theme = useTheme();
	const [searchTerm, setSearchTerm] = useState('');
	const [relativeWeeks, setRelativeWeeks] = useState<number>(0);
	const isCurrentOrFutureWeek = relativeWeeks >= 0;
	const upcomingWeekOffset = Math.max(relativeWeeks, 0) + 1;
	const {
		data: upcomingActivities,
		isLoading: isLoadingUpcomingActivities,
		error: errorUpcomingActivities,
	} = useQuery<Activity[]>({
		queryKey: ['upcoming-activities', upcomingWeekOffset],
		queryFn: () => fetch(`${baseUrl}/upcoming-week/${upcomingWeekOffset}`).then((res) => res.json()),
		enabled: isCurrentOrFutureWeek,
		staleTime: 30 * 60 * 1000, // 30 minutes - updates more frequently
	});
	const {
		data: archivedActivities,
		isLoading: isLoadingArchivedActivities,
		error: errorArchivedActivities,
	} = useQuery<Activity[]>({
		queryKey: ['archived-activities', relativeWeeks],
		queryFn: () => fetch(`${baseUrl}/past-week/${relativeWeeks * -1}`).then((res) => res.json()),
		enabled: relativeWeeks < 0,
		staleTime: 12 * 60 * 60 * 1000, // 12 hours - archived data changes less frequently
	});
	const data = relativeWeeks < 0 ? archivedActivities : upcomingActivities;
	const error = relativeWeeks < 0 ? errorArchivedActivities : errorUpcomingActivities;
	const loading = relativeWeeks < 0 ? isLoadingArchivedActivities : isLoadingUpcomingActivities;
	const isPreviousDisabled = relativeWeeks < calculateWeeksToGivenDate();

	// Let arrow keys page between weeks, unless the user is typing (e.g. in the search box)
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			const tagName = target?.tagName;
			if (tagName === 'INPUT' || tagName === 'TEXTAREA' || target?.isContentEditable) {
				return;
			}
			if (event.key === 'ArrowLeft' && !isPreviousDisabled) {
				setRelativeWeeks((current) => current - 1);
			} else if (event.key === 'ArrowRight') {
				setRelativeWeeks((current) => current + 1);
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isPreviousDisabled]);

	// Get week date range for display
	const getCurrentWeekRange = () => {
		const today = new Date();
		const startOfWeek = new Date(today);
		const dayOfWeek = today.getDay();
		const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
		startOfWeek.setDate(diff + relativeWeeks * 7);

		const endOfWeek = new Date(startOfWeek);
		endOfWeek.setDate(startOfWeek.getDate() + 6);

		const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
		return `${startOfWeek.toLocaleDateString('en-US', options)} - ${endOfWeek.toLocaleDateString('en-US', options)}`;
	};

	if (error)
		return (
			<Alert severity="error" sx={{ mt: 2 }}>
				Error fetching data: {error instanceof Error ? error.message : 'Unknown error'}
			</Alert>
		);

	return (
		<>
			<Paper
				elevation={0}
				sx={{
					p: 2,
					mb: 3,
					backgroundColor: theme.palette.background.paper,
					borderRadius: 2,
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
					<CalendarTodayIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
					<Typography variant="h6" fontWeight="bold">
						{relativeWeeks === 0
							? 'This Week'
							: relativeWeeks < 0
							? `${Math.abs(relativeWeeks)} Week${Math.abs(relativeWeeks) > 1 ? 's' : ''} Ago`
							: `${relativeWeeks} Week${relativeWeeks > 1 ? 's' : ''} Ahead`}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
						({getCurrentWeekRange()})
					</Typography>
				</Box>

				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
					<Tooltip title="Previous week (←)">
						<span style={{ display: 'flex', flex: '1 1 auto' }}>
							<Button
								variant="outlined"
								disabled={isPreviousDisabled}
								onClick={() => setRelativeWeeks(relativeWeeks - 1)}
								startIcon={<ArrowBackIcon />}
								sx={{ flex: { xs: '1', sm: '0 0 auto' }, width: '100%' }}
							>
								Previous
							</Button>
						</span>
					</Tooltip>
					<Button
						variant="outlined"
						disabled={relativeWeeks === 0}
						onClick={() => setRelativeWeeks(0)}
						startIcon={<TodayIcon />}
						sx={{ flex: { xs: '1', sm: '0 0 auto' } }}
					>
						This Week
					</Button>
					<SearchBar setSearchTerm={setSearchTerm} />
					<Tooltip title="Next week (→)">
						<span style={{ display: 'flex', flex: '1 1 auto' }}>
							<Button
								variant="outlined"
								onClick={() => setRelativeWeeks(relativeWeeks + 1)}
								endIcon={<ArrowForwardIcon />}
								sx={{ flex: { xs: '1', sm: '0 0 auto' }, width: '100%' }}
							>
								Next
							</Button>
						</span>
					</Tooltip>
				</Stack>
			</Paper>

			<Box>
				<Grid searchTerm={searchTerm} activities={data} relativeWeeks={relativeWeeks} loading={loading} />
			</Box>

			<Drawer />
		</>
	);
};
